import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import LoadingState from "@/components/base/LoadingState";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import UploadField from "@/components/base/UploadField";

/* ─── Types ────────────────────────────────────────────────── */
type DomainRequest = {
  requested_domain: string;
  status: string;
  created_at: string;
};

type BrandingForm = {
  /* Identity */
  business_name: string;
  logo_url: string;
  tagline: string;
  description: string;
  /* Colors */
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  /* Contact */
  phone: string;
  email: string;
  website: string;
  address: string;
  instagram_url: string;
  facebook_url: string;
  tiktok_url: string;
  whatsapp_number: string;
  /* Public */
  show_powered_by: boolean;
  public_footer_text: string;
};

const DEFAULT: BrandingForm = {
  business_name: "",
  logo_url: "",
  tagline: "",
  description: "",
  primary_color: "#C8A96E",
  secondary_color: "#0f172a",
  accent_color: "#e2c98a",
  phone: "",
  email: "",
  website: "",
  address: "",
  instagram_url: "",
  facebook_url: "",
  tiktok_url: "",
  whatsapp_number: "",
  show_powered_by: true,
  public_footer_text: "",
};

type Tab = "identity" | "colors" | "contact" | "public" | "preview";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "identity", label: "Identity",        icon: "ri-store-2-line" },
  { id: "colors",   label: "Colors",          icon: "ri-palette-line" },
  { id: "contact",  label: "Contact",         icon: "ri-contacts-line" },
  { id: "public",   label: "Public Branding", icon: "ri-global-line" },
  { id: "preview",  label: "Preview",         icon: "ri-eye-line" },
];

/* ─── Inline field ─────────────────────────────────────────── */
function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-slate-600 text-xs font-medium mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="text-slate-400 text-[11px] mt-1">{hint}</p>}
    </div>
  );
}

function TextInput({
  name, value, onChange, placeholder, type = "text",
}: {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/70 focus:bg-white transition-colors"
    />
  );
}

/* ─── ColorPicker row ───────────────────────────────────────── */
function ColorPicker({
  label, name, value, onChange,
}: {
  label: string; name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
      <input
        type="color"
        name={name}
        value={value}
        onChange={onChange}
        className="w-12 h-12 rounded-lg border border-slate-200 bg-transparent cursor-pointer p-0.5 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <p className="text-slate-700 text-sm font-medium mb-0.5">{label}</p>
        <input
          name={name}
          value={value}
          onChange={onChange}
          className="w-full bg-white border border-slate-200 rounded-md px-2.5 py-1.5 text-xs text-[#0f172a] font-mono outline-none focus:border-[#C8A96E]/70 transition-colors"
        />
      </div>
      <div className="w-8 h-8 rounded-full border border-slate-200 shrink-0" style={{ backgroundColor: value }} />
    </div>
  );
}

/* ─── Social input ─────────────────────────────────────────── */
function SocialInput({
  icon, name, value, onChange, placeholder,
}: {
  icon: string; name: string; value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 focus-within:border-[#C8A96E]/70 focus-within:bg-white transition-colors">
      <span className="w-5 h-5 flex items-center justify-center shrink-0">
        <i className={`${icon} text-slate-400 text-sm`}></i>
      </span>
      <input
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-sm text-[#0f172a] placeholder-slate-400 outline-none"
      />
    </div>
  );
}

/* ─── SaveBar ───────────────────────────────────────────────── */
function SaveBar({
  saving, saved, error, onSave,
}: {
  saving: boolean; saved: boolean; error: string | null;
  onSave: () => void;
}) {
  return (
    <div className="flex items-center justify-between pt-5 border-t border-slate-100 mt-6">
      <div className="text-xs">
        {error && (
          <span className="flex items-center gap-1.5 text-red-500">
            <i className="ri-error-warning-line"></i> {error}
          </span>
        )}
        {saved && !error && (
          <span className="flex items-center gap-1.5 text-emerald-600">
            <i className="ri-check-double-line"></i> Saved successfully
          </span>
        )}
        {!saved && !error && (
          <span className="text-slate-400">Changes are saved per section</span>
        )}
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="flex items-center gap-2 px-6 py-2.5 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            Saving…
          </>
        ) : (
          <>
            <i className="ri-save-3-line"></i>
            Save Changes
          </>
        )}
      </button>
    </div>
  );
}

/* ─── Portal Preview ────────────────────────────────────────── */
function PortalPreview({ form }: { form: BrandingForm }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="px-5 py-4" style={{ backgroundColor: form.secondary_color }}>
        <div className="flex items-center gap-3">
          {form.logo_url ? (
            <img src={form.logo_url} alt="Logo" className="h-7 w-auto object-contain rounded" />
          ) : (
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: form.primary_color }}
            >
              <i className="ri-camera-2-line text-white text-base"></i>
            </div>
          )}
          <div>
            <p className="text-white text-sm font-bold leading-tight">
              {form.business_name || "Studio Name"}
            </p>
            {form.tagline && (
              <p className="text-white/50 text-[10px] leading-tight mt-0.5">{form.tagline}</p>
            )}
          </div>
        </div>
      </div>
      {/* Body */}
      <div className="bg-[#f8f9fb] p-4 space-y-3">
        {/* Contract card */}
        <div className="bg-white rounded-xl p-4 border border-slate-100">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-slate-800 text-sm font-semibold">Wedding Photography</p>
              <p className="text-slate-400 text-xs mt-0.5">June 15, 2025 · Miami, FL</p>
            </div>
            <span
              className="text-[10px] rounded-full px-2.5 py-0.5 font-semibold shrink-0"
              style={{ backgroundColor: form.primary_color + "22", color: form.primary_color }}
            >
              Active
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="flex-1 py-2 rounded-lg text-xs font-semibold text-white"
              style={{ backgroundColor: form.primary_color }}
            >
              View Contract
            </button>
            <button
              className="px-3 py-2 rounded-lg text-xs font-medium text-slate-600 border border-slate-200 bg-white"
            >
              Pay
            </button>
          </div>
        </div>
        {/* Quick actions */}
        <div className="grid grid-cols-3 gap-2">
          {["Photos", "Gallery", "Messages"].map((item) => (
            <div key={item} className="bg-white rounded-xl p-3 text-center border border-slate-100">
              <div
                className="w-8 h-8 rounded-lg mx-auto mb-1.5 flex items-center justify-center"
                style={{ backgroundColor: form.accent_color + "33" }}
              >
                <i className={`text-sm ${item === "Photos" ? "ri-image-line" : item === "Gallery" ? "ri-gallery-line" : "ri-message-3-line"}`}
                  style={{ color: form.accent_color }} />
              </div>
              <p className="text-slate-600 text-[10px] font-medium">{item}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Footer */}
      <div className="px-4 py-2.5 bg-white border-t border-slate-100 flex items-center justify-between">
        {form.public_footer_text ? (
          <p className="text-slate-400 text-[10px]">{form.public_footer_text}</p>
        ) : (
          <p className="text-slate-400 text-[10px]">{form.business_name || "Studio"} © 2025</p>
        )}
        {form.show_powered_by && (
          <p className="text-slate-300 text-[10px]">Powered by platform</p>
        )}
      </div>
    </div>
  );
}

/* ─── Website Preview ───────────────────────────────────────── */
function WebsitePreview({ form }: { form: BrandingForm }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200">
      {/* Nav */}
      <div className="px-5 py-3 bg-white border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {form.logo_url ? (
            <img src={form.logo_url} alt="Logo" className="h-6 object-contain" />
          ) : (
            <div
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: form.primary_color }}
            >
              <i className="ri-camera-2-line text-white text-xs"></i>
            </div>
          )}
          <span className="text-slate-800 text-xs font-bold">{form.business_name || "Studio"}</span>
        </div>
        <div className="flex items-center gap-3">
          {["Portfolio", "Services", "Contact"].map((item) => (
            <span key={item} className="text-slate-500 text-[10px]">{item}</span>
          ))}
        </div>
      </div>
      {/* Hero */}
      <div
        className="px-6 py-10 flex flex-col items-center text-center"
        style={{ background: `linear-gradient(135deg, ${form.secondary_color}, ${form.secondary_color}cc)` }}
      >
        <p
          className="text-[10px] font-semibold uppercase tracking-widest mb-2"
          style={{ color: form.primary_color }}
        >
          Photography Studio
        </p>
        <h2 className="text-white text-lg font-bold mb-2 leading-tight">
          {form.tagline || "Capturing timeless moments"}
        </h2>
        {form.description && (
          <p className="text-white/60 text-[10px] mb-4 max-w-[220px] leading-relaxed">
            {form.description.substring(0, 80)}{form.description.length > 80 ? "…" : ""}
          </p>
        )}
        <button
          className="px-4 py-1.5 rounded-full text-[10px] font-semibold text-white"
          style={{ backgroundColor: form.primary_color }}
        >
          Book a Session
        </button>
      </div>
      {/* Footer */}
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{ backgroundColor: form.secondary_color }}
      >
        <p className="text-white/40 text-[10px]">
          {form.public_footer_text || `© 2025 ${form.business_name || "Studio"}`}
        </p>
        <div className="flex items-center gap-2">
          {form.instagram_url && <i className="ri-instagram-line text-white/40 text-xs"></i>}
          {form.facebook_url  && <i className="ri-facebook-line text-white/40 text-xs"></i>}
          {form.tiktok_url    && <i className="ri-tiktok-line text-white/40 text-xs"></i>}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─────────────────────────────────────────── */
export default function AdminBranding() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [brandingExists, setBrandingExists] = useState(false);
  const [domainRequest, setDomainRequest] = useState<DomainRequest | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("identity");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<BrandingForm>(DEFAULT);

  /* ─── Fetch ── */
  const fetchData = useCallback(async (tid: string) => {
    const [brandingRes, domainRes] = await Promise.all([
      supabase.from("saas_tenant_branding").select("*").eq("tenant_id", tid).maybeSingle(),
      supabase
        .from("tenant_domain_requests")
        .select("requested_domain, status, created_at")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (brandingRes.data) {
      setBrandingExists(true);
      const b = brandingRes.data;
      setForm({
        business_name:    b.business_name    ?? "",
        logo_url:         b.logo_url         ?? "",
        tagline:          b.tagline          ?? "",
        description:      b.description      ?? "",
        primary_color:    b.primary_color    ?? "#C8A96E",
        secondary_color:  b.secondary_color  ?? "#0f172a",
        accent_color:     b.accent_color     ?? "#e2c98a",
        phone:            b.phone            ?? "",
        email:            b.email            ?? "",
        website:          b.website          ?? "",
        address:          b.address          ?? "",
        instagram_url:    b.instagram_url    ?? "",
        facebook_url:     b.facebook_url     ?? "",
        tiktok_url:       b.tiktok_url       ?? "",
        whatsapp_number:  b.whatsapp_number  ?? "",
        show_powered_by:  b.show_powered_by  ?? true,
        public_footer_text: b.public_footer_text ?? "",
      });
    } else {
      setBrandingExists(false);
    }

    if (domainRes.data) setDomainRequest(domainRes.data as DomainRequest);
    setLoading(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchData(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  /* ─── Handlers ── */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const val = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;
    setForm((prev) => ({ ...prev, [name]: val }));
  };

  const handleSave = async () => {
    if (!tenantId) return;
    setSaving(true);
    setSaved(false);
    setSaveError(null);

    const payload = {
      ...form,
      tenant_id: tenantId,
      updated_at: new Date().toISOString(),
    };

    let err: { message: string } | null = null;

    if (brandingExists) {
      const res = await supabase
        .from("saas_tenant_branding")
        .update(payload)
        .eq("tenant_id", tenantId);
      err = res.error;
    } else {
      const res = await supabase
        .from("saas_tenant_branding")
        .insert(payload);
      err = res.error;
      if (!err) setBrandingExists(true);
    }

    setSaving(false);
    if (err) {
      setSaveError(err.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  /* ─── Domain status badge ── */
  function DomainBadge({ status }: { status: string }) {
    const map: Record<string, { label: string; cls: string }> = {
      approved:   { label: "Approved",   cls: "bg-emerald-100 text-emerald-700" },
      pending:    { label: "Pending",    cls: "bg-amber-100 text-amber-700" },
      rejected:   { label: "Rejected",  cls: "bg-red-100 text-red-600" },
    };
    const d = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-600" };
    return (
      <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${d.cls}`}>
        {d.label}
      </span>
    );
  }

  /* ─── Tab content ── */
  function IdentityTab() {
    return (
      <div className="space-y-5">
        <Field label="Business Name *" hint="Shown throughout the portal and website">
          <TextInput
            name="business_name"
            value={form.business_name}
            onChange={handleChange}
            placeholder="Your Studio Name"
          />
        </Field>

        <UploadField
          label="Logo"
          value={form.logo_url}
          onChange={(url) => setForm((prev) => ({ ...prev, logo_url: url }))}
          accept="image/*"
          hint="PNG, JPG, SVG or WebP — recommended size 400×120px"
          urlPlaceholder="https://..."
          showPreview
          tenantId={tenantId ?? ""}
          entityType="branding"
          folder="branding"
          maxSizeMB={10}
        />

        <Field label="Tagline" hint="Short headline displayed below your name">
          <TextInput
            name="tagline"
            value={form.tagline}
            onChange={handleChange}
            placeholder="Capturing timeless moments"
          />
        </Field>

        <Field label="Description" hint="Shown on your public website and portal bio">
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={4}
            placeholder="Tell clients what makes your studio special…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/70 focus:bg-white transition-colors resize-none"
          />
        </Field>
      </div>
    );
  }

  function ColorsTab() {
    return (
      <div className="space-y-5">
        <div className="space-y-3">
          <ColorPicker label="Primary Color"   name="primary_color"   value={form.primary_color}   onChange={handleChange} />
          <ColorPicker label="Secondary Color" name="secondary_color" value={form.secondary_color} onChange={handleChange} />
          <ColorPicker label="Accent Color"    name="accent_color"    value={form.accent_color}    onChange={handleChange} />
        </div>

        {/* Live palette preview */}
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <p className="text-slate-500 text-xs font-medium mb-3">Live Color Preview</p>
          <div className="space-y-2">
            {/* Simulated button row */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="px-4 py-2 rounded-lg text-white text-xs font-semibold"
                style={{ backgroundColor: form.primary_color }}
              >
                Primary Button
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white text-xs font-semibold"
                style={{ backgroundColor: form.secondary_color }}
              >
                Secondary Button
              </button>
              <span
                className="px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: form.accent_color + "33", color: form.accent_color }}
              >
                Accent Badge
              </span>
            </div>
            {/* Simulated nav */}
            <div
              className="rounded-lg px-4 py-2.5 flex items-center justify-between"
              style={{ backgroundColor: form.secondary_color }}
            >
              <span className="text-white text-xs font-bold">{form.business_name || "Studio"}</span>
              <div className="flex items-center gap-3">
                <span className="text-white/60 text-[10px]">Portfolio</span>
                <button
                  className="px-3 py-1 rounded-md text-[10px] font-semibold text-white"
                  style={{ backgroundColor: form.primary_color }}
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          {[
            { label: "Primary", color: form.primary_color },
            { label: "Secondary", color: form.secondary_color },
            { label: "Accent", color: form.accent_color },
          ].map(({ label, color }) => (
            <div key={label} className="flex-1 text-center">
              <div
                className="h-14 rounded-xl mb-2 border border-slate-200"
                style={{ backgroundColor: color }}
              />
              <p className="text-slate-500 text-[10px] font-mono">{color}</p>
              <p className="text-slate-400 text-[10px]">{label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function ContactTab() {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Phone">
            <TextInput name="phone" value={form.phone} onChange={handleChange} placeholder="+1 (555) 000-0000" />
          </Field>
          <Field label="Email">
            <TextInput name="email" type="email" value={form.email} onChange={handleChange} placeholder="hello@studio.com" />
          </Field>
          <Field label="Website">
            <TextInput name="website" value={form.website} onChange={handleChange} placeholder="https://yourstudio.com" />
          </Field>
          <Field label="Address">
            <TextInput name="address" value={form.address} onChange={handleChange} placeholder="Street, City, State, ZIP" />
          </Field>
        </div>

        <div className="pt-2">
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-share-line text-slate-400"></i>
            Social Links
          </p>
          <div className="space-y-2.5">
            <SocialInput
              icon="ri-instagram-line"
              name="instagram_url"
              value={form.instagram_url}
              onChange={handleChange}
              placeholder="https://instagram.com/yourstudio"
            />
            <SocialInput
              icon="ri-facebook-line"
              name="facebook_url"
              value={form.facebook_url}
              onChange={handleChange}
              placeholder="https://facebook.com/yourstudio"
            />
            <SocialInput
              icon="ri-tiktok-line"
              name="tiktok_url"
              value={form.tiktok_url}
              onChange={handleChange}
              placeholder="https://tiktok.com/@yourstudio"
            />
            <SocialInput
              icon="ri-whatsapp-line"
              name="whatsapp_number"
              value={form.whatsapp_number}
              onChange={handleChange}
              placeholder="+1 555 000 0000 (for WhatsApp link)"
            />
          </div>
        </div>
      </div>
    );
  }

  function PublicBrandingTab() {
    return (
      <div className="space-y-5">
        {/* Show powered by toggle */}
        <div className="flex items-start justify-between p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div>
            <p className="text-slate-700 text-sm font-semibold">Show "Powered by" badge</p>
            <p className="text-slate-400 text-xs mt-0.5">Display platform attribution in client portal footer</p>
          </div>
          <button
            type="button"
            onClick={() => setForm((prev) => ({ ...prev, show_powered_by: !prev.show_powered_by }))}
            className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer shrink-0 ${
              form.show_powered_by ? "bg-[#C8A96E]" : "bg-slate-200"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                form.show_powered_by ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Public footer text */}
        <Field
          label="Public Footer Text"
          hint="Shown at the bottom of the client portal and public website. Leave empty to use default."
        >
          <TextInput
            name="public_footer_text"
            value={form.public_footer_text}
            onChange={handleChange}
            placeholder={`© 2025 ${form.business_name || "Your Studio"} · All rights reserved`}
          />
        </Field>

        {/* Custom Domain (readonly) */}
        <div className="pt-2">
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-global-line text-slate-400"></i>
            Custom Domain
          </p>
          {domainRequest ? (
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-700 text-sm font-semibold font-mono">
                  {domainRequest.requested_domain}
                </p>
                <DomainBadge status={domainRequest.status} />
              </div>
              <p className="text-slate-400 text-xs">
                Requested on {new Date(domainRequest.created_at).toLocaleDateString()}
              </p>
              {domainRequest.status === "approved" && (
                <div className="mt-3 flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-lg p-2.5">
                  <i className="ri-check-double-line text-sm"></i>
                  <p className="text-xs font-medium">Your custom domain is active.</p>
                </div>
              )}
              {domainRequest.status === "pending" && (
                <div className="mt-3 flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-2.5">
                  <i className="ri-time-line text-sm"></i>
                  <p className="text-xs">Domain under review by platform admin. This may take 24–48h.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 rounded-xl border border-dashed border-slate-200 p-5 text-center">
              <div className="w-10 h-10 flex items-center justify-center bg-slate-100 rounded-xl mx-auto mb-2">
                <i className="ri-global-line text-slate-400 text-xl"></i>
              </div>
              <p className="text-slate-600 text-sm font-medium mb-1">No custom domain yet</p>
              <p className="text-slate-400 text-xs mb-3">
                You can request a custom domain from{" "}
                <span className="font-medium">Settings → Domain</span>
              </p>
              <button
                type="button"
                onClick={() => navigate("/admin/settings/domain")}
                className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-[#C8A96E]/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                Request Domain →
              </button>
            </div>
          )}
        </div>

        {/* SEO Hint */}
        <div className="bg-[#C8A96E]/10 rounded-xl p-4 border border-[#C8A96E]/20">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 flex items-center justify-center shrink-0">
              <i className="ri-lightbulb-flash-line text-[#a6853e]"></i>
            </div>
            <div>
              <p className="text-[#a6853e] text-xs font-semibold mb-1">Branding Tip</p>
              <p className="text-slate-600 text-xs leading-relaxed">
                A strong logo and consistent colors increase client trust and brand recognition.
                Your primary color is used on all CTA buttons, and your secondary color sets the portal header.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function PreviewTab() {
    return (
      <div className="space-y-6">
        <div>
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-smartphone-line text-slate-400"></i>
            Client Portal Preview
          </p>
          <PortalPreview form={form} />
        </div>
        <div>
          <p className="text-slate-600 text-xs font-semibold mb-3 flex items-center gap-2">
            <i className="ri-computer-line text-slate-400"></i>
            Public Website Preview
          </p>
          <WebsitePreview form={form} />
        </div>
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 text-center">
          <i className="ri-information-line text-slate-400 text-lg mb-2 block"></i>
          <p className="text-slate-500 text-xs">
            This is a simplified preview. The actual portal and website reflect your branding in real-time after saving.
          </p>
        </div>
      </div>
    );
  }

  const tabContent: Record<Tab, React.ReactNode> = {
    identity: <IdentityTab />,
    colors:   <ColorsTab />,
    contact:  <ContactTab />,
    public:   <PublicBrandingTab />,
    preview:  <PreviewTab />,
  };

  /* ─── Render ── */
  return (
    <AdminLayout>
      <AdminHeader
        title="Branding"
        subtitle="Manage your studio identity, colors and public presence"
      />
      <div className="px-4 md:px-8 py-6">
        {loading ? (
          <LoadingState />
        ) : (
          <div className="max-w-4xl">
            {/* Status bar */}
            <div className="flex items-center gap-3 mb-6 p-3.5 bg-white rounded-xl border border-slate-200">
              <div className={`w-2 h-2 rounded-full shrink-0 ${brandingExists ? "bg-emerald-400" : "bg-amber-400"}`} />
              <p className="text-slate-600 text-xs flex-1">
                {brandingExists
                  ? "Branding data loaded from Supabase — all changes save immediately."
                  : "No branding saved yet. Fill in the form and save to create your brand profile."}
              </p>
              <span className="text-slate-400 text-[10px] font-mono bg-slate-50 px-2.5 py-1 rounded-md">
                tenant: {tenantId?.split("-")[0]}…
              </span>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1.5 mb-6 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id
                      ? "bg-[#0f172a] text-white"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              {tabContent[activeTab]}

              {/* Save bar — hidden on preview tab */}
              {activeTab !== "preview" && (
                <SaveBar
                  saving={saving}
                  saved={saved}
                  error={saveError}
                  onSave={handleSave}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}